import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import toast from 'react-hot-toast'
import SignUpForm from './SignUpForm'
import CustomConfirmationModal from '../../components/CustomConfirmationModal'
import { useApi } from '../../hooks/useApi'
import FormModal from '../FormModal'

interface User {
  _id: number
  email: string
  updatedAt: string
  roles: string[]
  password?: string
}

const UserList: React.FC = () => {
  const { callApi } = useApi()
  const [users, setUsers] = useState<User[] | null>(null)
  const [editedProps, setEditedProps] = useState<User | null>(null)
  const [editItem, setEditItem] = useState<User | null>(null)
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [customModalOpen, setCustomModalOpen] = useState(false)
  const [deleteParams, setDeleteParams] = useState<User | null>(null)
  const [addNew, setAddNew] = useState(false)

  const fetchUsers = async () => {
    try {
      const response = await callApi('/admin/users')
      if (response) setUsers(response)
    } catch (e) {
      console.error('Error fetching users:', e)
    }
  }

  useEffect(() => { fetchUsers() }, [])

  const handleEdit = (item: User, index: number) => {
    setEditIndex(index)
    setEditItem(item)
    setEditedProps({ ...item })
  }

  const handleCancelEdit = () => setEditIndex(null)

  const handleSaveEdit = async () => {
    try {
      await callApi(`/admin/users/${editedProps?._id}`, { ...editedProps }, 'PUT')
      fetchUsers()
    } catch (e) {
      console.error('Error updating user:', e)
    }
    setEditIndex(null)
  }

  const handleDeleteClick = (item: User) => {
    setDeleteParams(item)
    setCustomModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    try {
      await callApi(`/admin/users/${deleteParams?._id}`, {}, 'DELETE')
      fetchUsers()
    } catch {}
    setCustomModalOpen(false)
  }

  if (!users) return <LoaderBar />

  return (
    <Wrap>


      <Header>
        <AddBtn onClick={() => setAddNew(!addNew)}>
          {addNew ? 'Hide form' : '+ Add user'}
        </AddBtn>
      </Header>

      {addNew && (
        <FormModal onConfirm={() => { fetchUsers(); setAddNew(false) }} onCancel={() => setAddNew(false)}>
          <SignUpForm onSuccess={() => { fetchUsers(); setAddNew(false) }} />
        </FormModal>
      )}

      {users.length > 0 && (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>User</Th>
                <Th>Roles</Th>
                <Th>Updated at</Th>
                <Th>Password</Th>
                <Th></Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => {
                const isEditing = editIndex === index
                return (
                  <tr key={user._id}>
                    <Td>
                      {isEditing ? (
                        <InlineInput
                          type="text"
                          value={editedProps?.email ?? ''}
                          onChange={(e) => setEditedProps({ ...editedProps!, email: e.target.value })}
                        />
                      ) : (
                        <EmailText>{user.email}</EmailText>
                      )}
                    </Td>
                    <Td>
                      {isEditing ? (
                        <InlineInput
                          type="text"
                          style={{ width: 200 }}
                          value={editedProps?.roles.join(',') ?? ''}
                          onChange={(e) => setEditedProps({ ...editedProps!, roles: e.target.value.replace(/\s/g, '').split(',') })}
                        />
                      ) : (
                        <Mono>{user.roles.join(', ')}</Mono>
                      )}
                    </Td>
                    <Td>
                      <Muted>
                        {new Date(user.updatedAt).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'short', day: 'numeric',
                          hour: 'numeric', minute: 'numeric',
                        })}
                      </Muted>
                    </Td>
                    <Td>
                      {isEditing ? (
                        <InlineInput
                          type="text"
                          placeholder="New password"
                          value={editedProps?.password ?? ''}
                          onChange={(e) => setEditedProps({ ...editedProps!, password: e.target.value })}
                        />
                      ) : (
                        <Muted>••••••</Muted>
                      )}
                    </Td>
                    <Td>
                      {isEditing ? (
                        <BtnGroup>
                          <SaveBtn onClick={handleSaveEdit}>Save</SaveBtn>
                          <GhostBtn onClick={handleCancelEdit}>Cancel</GhostBtn>
                        </BtnGroup>
                      ) : (
                        <EditBtn onClick={() => handleEdit(user, index)}>Edit</EditBtn>
                      )}
                    </Td>
                    <Td>
                      <DeleteBtn onClick={() => handleDeleteClick(user)}>Delete</DeleteBtn>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {customModalOpen && (
        <CustomConfirmationModal
          message="Are you sure you want to delete this user?"
          onConfirm={handleConfirmDelete}
          onCancel={() => setCustomModalOpen(false)}
        />
      )}
    </Wrap>
  )
}

export default UserList

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
`

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const LoaderBar = styled.div`
  height: 2px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(111, 92, 230, 0.5), transparent);
    animation: ${shimmer} 1.4s ease infinite;
  }
`

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  color: #a1a1a1;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`

const Td = styled.td`
  padding: 10px 12px;
  color: #a0a0a0;
  font-size: 13px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  tr:last-child & { border-bottom: none; }
`

const EmailText = styled.span`color: #ededed; font-size: 13px;`

const Mono = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
  color: #a0a0a0;
`

const Muted = styled.span`color: #777; font-size: 12px;`

const InlineInput = styled.input`
  background: #0d0d0d;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: #ededed;
  font-size: 12px;
  padding: 0 8px;
  height: 26px;
  width: 140px;
  outline: none;
  &:focus { border-color: rgba(111, 92, 230, 0.5); }
`

const BtnGroup = styled.div`display: flex; gap: 4px;`

const ActionBtn = styled.button`
  height: 26px; padding: 0 9px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px; color: #a0a0a0; font-size: 11px; cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
`

const EditBtn = styled(ActionBtn)`
  &:hover { border-color: rgba(111, 92, 230, 0.4); color: #a89aff; }
`

const SaveBtn = styled(ActionBtn)`
  &:hover { border-color: rgba(51, 194, 176, 0.4); color: #33C2B0; }
`

const GhostBtn = styled(ActionBtn)``

const DeleteBtn = styled(ActionBtn)`
  &:hover { border-color: rgba(255, 68, 68, 0.4); color: #ff4444; }
`

const AddBtn = styled.button`
  height: 28px;
  padding: 0 12px;
  background: rgba(111, 92, 230, 0.1);
  border: 1px solid rgba(111, 92, 230, 0.3);
  border-radius: 5px;
  color: #a89aff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  &:hover { background: rgba(111, 92, 230, 0.2); border-color: rgba(111, 92, 230, 0.5); }
`
